// Panneau Navigateur (02/10) : chaque resultat d'outil du navigateur (browser_open, browser_read,
// browser_click, browser_type, browser_login du serveur MCP des outils metier) porte, a cote de
// son texte, un bloc ressource MCP qui dit ou regarder le navigateur du membre :
//   { type: "resource", resource: { uri: "astraly-navigateur://session",
//     mimeType: "application/vnd.astraly.navigateur+json", text: "{streamUrl, inputUrl, etatUrl,
//     url, titre}" } }
// Les adresses sont relatives a la RACINE de l'hote public du chat (pas au chemin /chat de la
// SPA) et le jeton `t` n'ouvre que le navigateur du membre, pendant 2 h. Chaque resultat porte un
// jeton neuf : on garde toujours le plus recent.

export const MIME_NAVIGATEUR = "application/vnd.astraly.navigateur+json";
export const URI_NAVIGATEUR = "astraly-navigateur://session";

export interface SessionNavigateur {
  streamUrl: string;
  inputUrl: string;
  etatUrl: string;
  url: string | null;
  titre: string | null;
}

// Forme minimale d'un bloc de sortie d'outil : evite d'importer les types du SDK MCP ici.
interface BlocDeSortie {
  type: string;
  resource?: unknown;
}

function estObjet(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

// Une adresse du navigateur reste sur le meme hote, sous /navigateur/ : ni schema, ni « // »
// (adresse relative au protocole, qui changerait d'hote), ni autre chemin de l'app.
function estAdresseNavigateur(v: unknown): v is string {
  return (
    typeof v === "string" &&
    v.startsWith("/navigateur/") &&
    !v.startsWith("//") &&
    !v.includes("\\")
  );
}

export function estBlocNavigateur(bloc: BlocDeSortie): boolean {
  return (
    bloc.type === "resource" &&
    estObjet(bloc.resource) &&
    bloc.resource.mimeType === MIME_NAVIGATEUR
  );
}

function lireBloc(bloc: BlocDeSortie): SessionNavigateur | null {
  if (!estBlocNavigateur(bloc) || !estObjet(bloc.resource)) {
    return null;
  }
  const texte = bloc.resource.text;
  if (typeof texte !== "string") {
    return null;
  }
  let champs: unknown;
  try {
    champs = JSON.parse(texte);
  } catch {
    return null;
  }
  if (
    !estObjet(champs) ||
    !estAdresseNavigateur(champs.streamUrl) ||
    !estAdresseNavigateur(champs.inputUrl) ||
    !estAdresseNavigateur(champs.etatUrl)
  ) {
    return null;
  }
  return {
    streamUrl: champs.streamUrl,
    inputUrl: champs.inputUrl,
    etatUrl: champs.etatUrl,
    url: typeof champs.url === "string" && champs.url ? champs.url : null,
    titre:
      typeof champs.titre === "string" && champs.titre ? champs.titre : null,
  };
}

/** Session portee par une sortie d'outil (le dernier bloc valide gagne), ou null. */
export function extraireSessionNavigateur(
  sortie: readonly BlocDeSortie[] | null | undefined
): SessionNavigateur | null {
  if (!sortie) {
    return null;
  }
  let trouvee: SessionNavigateur | null = null;
  for (const bloc of sortie) {
    trouvee = lireBloc(bloc) ?? trouvee;
  }
  return trouvee;
}

export function contientSessionNavigateur(
  sortie: readonly BlocDeSortie[] | null | undefined
): boolean {
  return extraireSessionNavigateur(sortie) !== null;
}

/** La sortie sans le bloc du jeton : c'est ce qui peut etre montre au membre. */
export function sansBlocNavigateur<T extends BlocDeSortie>(
  sortie: readonly T[]
): T[] {
  return sortie.filter((bloc) => !estBlocNavigateur(bloc));
}

export interface ActionAvecSortie {
  sId: string;
  createdAt: number;
  status: string;
  output: readonly BlocDeSortie[] | null;
}

export interface SessionDatee {
  session: SessionNavigateur;
  actionId: string;
  horodatage: number;
}

/** Parmi des actions, la session du resultat termine le plus recent. */
export function derniereSessionDesActions(
  actions: readonly ActionAvecSortie[]
): SessionDatee | null {
  let meilleure: SessionDatee | null = null;
  for (const action of actions) {
    if (action.status !== "succeeded") {
      continue;
    }
    const session = extraireSessionNavigateur(action.output);
    if (!session) {
      continue;
    }
    if (!meilleure || action.createdAt >= meilleure.horodatage) {
      meilleure = {
        session,
        actionId: action.sId,
        horodatage: action.createdAt,
      };
    }
  }
  return meilleure;
}
